/// <reference types="@types/google.maps" />

declare global {
  interface Window {
    google?: typeof google;
  }
}

const API_KEY = import.meta.env.VITE_FRONTEND_FORGE_API_KEY;
const FORGE_BASE_URL =
  import.meta.env.VITE_FRONTEND_FORGE_API_URL ||
  "https://forge.butterfly-effect.dev";
const MAPS_PROXY_URL = `${FORGE_BASE_URL}/v1/maps/proxy`;
const SCRIPT_SELECTOR = 'script[data-partition-stalls-google-maps="true"]';
const MAPS_READY_TIMEOUT_MS = 8_000;
const MAPS_READY_POLL_MS = 50;

let mapsPromise: Promise<typeof google.maps> | null = null;

type MapsReader = () => typeof google.maps | undefined;

export async function waitForGoogleMapsReady(
  readMaps: MapsReader,
  timeoutMs = MAPS_READY_TIMEOUT_MS,
  pollMs = MAPS_READY_POLL_MS,
): Promise<typeof google.maps> {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() <= deadline) {
    const maps = readMaps();
    if (maps && typeof maps.importLibrary === "function") return maps;
    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, pollMs));
  }

  throw new Error("Google Maps loaded without the expected library interface.");
}

export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Google Maps is available only in the browser."));
  }

  if (window.google?.maps && typeof window.google.maps.importLibrary === "function") {
    return Promise.resolve(window.google.maps);
  }

  if (!API_KEY) {
    return Promise.reject(new Error("The authenticated Google Maps connection is unavailable."));
  }

  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise<typeof google.maps>((resolve, reject) => {
    const awaitReadiness = () => {
      void waitForGoogleMapsReady(() => window.google?.maps).then(resolve, reject);
    };
    const existingScript = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
    if (existingScript) {
      existingScript.addEventListener("load", awaitReadiness, { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Google Maps failed to load.")), { once: true });
      awaitReadiness();
      return;
    }

    const script = document.createElement("script");
    script.dataset.partitionStallsGoogleMaps = "true";
    script.src = `${MAPS_PROXY_URL}/maps/api/js?key=${encodeURIComponent(API_KEY)}&v=weekly&loading=async&libraries=marker,places,geocoding,geometry`;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = awaitReadiness;
    script.onerror = () => reject(new Error("Google Maps failed to load."));
    document.head.appendChild(script);
  }).catch((error) => {
    mapsPromise = null;
    throw error;
  });

  return mapsPromise;
}
